/**
 * Aparência de uma estação no mapa — funções puras (testadas em `look.test.ts`).
 *
 * A camada separa o que é ESTÁVEL do que é TEMPORAL, e essa separação é a
 * decisão central do desenho:
 *
 * - **a gota** responde "choveu aqui no período?" — cor pelo total das 24 h.
 *   Não muda de um quadro para o outro. Com 569 pontos, recolorir todos a cada
 *   quadro vira pisca-pisca e esconde a informação em vez de mostrá-la;
 * - **o halo** responde "está chovendo agora?" — só aparece nas fatias em que
 *   a estação registrou chuva. É a única coisa que acende e apaga enquanto o
 *   loop roda, então o piscar passa a significar exatamente uma coisa.
 *
 * TODAS as estações são desenhadas, sempre, em três estados:
 *
 * | Estado | Desenho | Significa |
 * |---|---|---|
 * | `chuva`   | gota cheia, cor pelo total do período | choveu nas últimas 24 h |
 * | `seca`    | gota vazada, contorno contínuo        | mediu e não choveu |
 * | `semDado` | gota vazada, contorno tracejado       | não reportou nada |
 *
 * `seca` e `semDado` não podem ser iguais: a primeira é uma medição, a segunda
 * é silêncio do sensor.
 */
import { corDe, raioDe } from './series';
import type { Leitura } from './series';

export type Estado = 'chuva' | 'seca' | 'semDado';

/** Cor do contorno das gotas vazadas — legível sobre nuvem branca e mata escura. */
export const COR_SECA = '#9fb4c7';
export const COR_SEM_DADO = '#7d8896';

export function estadoDe(total: Leitura): Estado {
  if (total.mm === null) return 'semDado';
  return total.mm > 0 ? 'chuva' : 'seca';
}

export interface Halo {
  /** Raio do anel em px. */
  raio: number;
  cor: string;
  largura: number;
  opacidade: number;
}

export interface Visual {
  estado: Estado;
  /** Raio base da gota em px. */
  raio: number;
  /** Preenchimento, ou null quando a gota é vazada. */
  preenchimento: string | null;
  contorno: string;
  larguraContorno: number;
  /** Tracejado do contorno (vazio = contínuo). */
  tracejado: number[];
  opacidade: number;
  /** Anel de "chovendo agora"; null quando não chove nesta fatia. */
  halo: Halo | null;
}

/** Acima disso o halo não cresce mais — chuva forte já se lê pela cor. */
const HALO_SATURA_MM = 6;

/**
 * Como desenhar uma estação.
 *
 * @param total chuva no período inteiro (estável entre quadros)
 * @param agora chuva na fatia do quadro exibido em mm; `0` = mediu e não
 *   choveu, `null` = não dá para saber. Nos dois casos não há halo, mas os
 *   dois não são a mesma coisa e a contagem os separa.
 */
export function visualDe(total: Leitura, agora: number | null, tamanho: number, opacidade: number): Visual {
  const estado = estadoDe(total);
  const halo = agora !== null && agora > 0 ? haloDe(agora, tamanho, opacidade) : null;

  if (estado === 'chuva') {
    return {
      estado,
      raio: raioDe(total.mm!, tamanho),
      preenchimento: corDe(total.mm!),
      contorno: 'rgba(255,255,255,0.75)',
      larguraContorno: 1,
      tracejado: [],
      opacidade,
      halo,
    };
  }
  if (estado === 'seca') {
    return {
      estado,
      // Estações sem chuva ficam discretas de propósito: com 569 pontos e chuva
      // em poucos deles, dar o mesmo peso a todos esconderia o sinal.
      raio: tamanho * 0.62,
      preenchimento: null,
      contorno: COR_SECA,
      larguraContorno: 1.1,
      tracejado: [],
      opacidade: opacidade * 0.85,
      halo,
    };
  }
  return {
    estado,
    raio: tamanho * 0.56,
    preenchimento: null,
    contorno: COR_SEM_DADO,
    larguraContorno: 1,
    tracejado: [1.5, 1.5],
    opacidade: opacidade * 0.6,
    halo,
  };
}

/** Anel de chuva no instante. Cresce com a intensidade, até saturar. */
export function haloDe(mm: number, tamanho: number, opacidade: number): Halo {
  const k = Math.min(1, mm / HALO_SATURA_MM);
  const a = Math.min(1, opacidade) * (0.55 + 0.35 * k);
  return {
    // De 1,4× a 1,9× a base: sempre fora da gota (que vai até 1,3×) e, na base de 7 px, com o
    // mesmo diâmetro máximo de quando a base era 5 e o halo ia a 2,4× (09/10/2026).
    raio: tamanho * (1.4 + 0.5 * k),
    // rgba direto: `ol/style/Style` não tem opacidade própria, ela vai na cor.
    cor: `rgba(127,227,255,${Math.round(a * 1000) / 1000})`,
    largura: 1.4 + 0.8 * k,
    opacidade: a,
  };
}

export interface Contagem {
  /** Choveu em algum momento do período. */
  chuva: number;
  /** Mediu e não choveu. */
  seca: number;
  /** Não reportou nada. */
  semDado: number;
  /** Recebendo chuva na fatia do quadro exibido. */
  agora: number;
}

export function contarEstados(
  totais: readonly Leitura[],
  agora: readonly (number | null)[] = [],
): Contagem {
  const c: Contagem = { chuva: 0, seca: 0, semDado: 0, agora: 0 };
  totais.forEach((t, i) => {
    c[estadoDe(t)]++;
    const a = agora[i];
    if (a !== null && a !== undefined && a > 0) c.agora++;
  });
  return c;
}
